<script setup lang="ts">
import { computed } from 'vue';
import { PRODUCT_NAME } from '@/constants';
import DocumentBasicInfoLayout from '@/components/common/adp-document/basic-info/document-basic-info-layout.vue';
import DocumentMediaName from '@/components/common/adp-document/basic-info/document-media-name.vue';
import NewArticleTag from '@/components/common/atoms/new-article-tag.vue';
import { AdpDocumentBasicInfo, isArticle } from '@/types';

// 記事系のデフォルト表示。doc_type が未設定のプレビュー用データ（テーマ設定プレビュー等）も
// ここにフォールバックするため、厳密な Article ではなく AdpDocumentBasicInfo を受け取る。
interface Props {
  adpDocument: AdpDocumentBasicInfo;
  size?: 'xm' | 'm' | 'l';
  imageVariant?: 'banner' | 'thumbnail';
  isNarrow?: boolean;
  isRelatedArticle?: boolean;
  isNewArticle?: boolean;
  trendCompanyText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xm',
  imageVariant: 'thumbnail',
  isNarrow: false,
  isRelatedArticle: false,
  isNewArticle: false,
});

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();

const loginRequired = computed(() =>
  isArticle(props.adpDocument)
    ? props.adpDocument.login_required ?? false
    : false,
);
</script>

<template>
  <DocumentBasicInfoLayout
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  >
    <template #publisher>
      <slot name="documentMediaName">
        <DocumentMediaName :publisher="adpDocument.media_name">
          <template #trailing>
            <div class="spacing-04"></div>
            <div v-if="loginRequired" class="media-type-label c-title-bold--xs">
              ログインが必要
            </div>
            <div
              v-else-if="adpDocument.is_alliance_media"
              class="media-type-label c-title-bold--xs"
            >
              {{ PRODUCT_NAME }}で読める有料記事
            </div>
          </template>
        </DocumentMediaName>
      </slot>
    </template>
    <template #dateExtra>
      <NewArticleTag v-if="isNewArticle" />
    </template>
    <template #boxBottom>
      <div class="trend-company c-text-regular--s" v-if="trendCompanyText">
        {{ trendCompanyText }}
      </div>
    </template>
  </DocumentBasicInfoLayout>
</template>

<style lang="scss" scoped>
.media-type-label {
  padding: 1px 8px;
  color: $color-gray800;
  background-color: $color-gray200;
  white-space: nowrap;
  border-radius: 2px;
}

.trend-company {
  margin-top: 8px;
  color: $color-gray800;
}
</style>
